import { useCallback, useState } from "react";
import { Controller, FieldErrors, SubmitHandler, useFormContext } from "react-hook-form";
import { getUnixTime } from "date-fns/esm";

import FormFieldRoleAssignment from "components/FormFields/FormFieldRoleAssignment";
import FormToggleField from "ds/components/Form/ToggleField";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import SegmentedControl from "ds/components/SegmentedControl";
import Typography from "ds/components/Typography";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useTypedContext from "hooks/useTypedContext";
import { ApiKeyType } from "types/generated";
import FormFieldApiKeyGroups from "views/Account/Settings/ApiKey/components/FormFieldApiKeyGroups";
import { UserManagementActivationStatus } from "views/Account/Settings/types";

import { ApiKeyCreateWizardContext } from "../../context";
import { ManageAccessTab } from "../../constants";
import { CreateApiKeyFields } from "../../types";
import useCreateApiKey from "../../useCreateApiKey";
import ApiKeyCreateFooter from "../../Footer";

type ApiKeyCreateStepManageAccessProps = {
  onSuccess: (id: string) => void;
};

const MANAGE_ACCESS_TAB_OPTIONS = [
  { label: "Roles", value: ManageAccessTab.Roles },
  { label: "IdP groups", value: ManageAccessTab.IdpGroups },
];

const ApiKeyCreateStepManageAccess = ({
  onSuccess,
}: ApiKeyCreateStepManageAccessProps) => {
  const { availableRoles, rolesLoading, spaceAdminRole, activationStatus } =
    useTypedContext(ApiKeyCreateWizardContext);

  const [activeTab, setActiveTab] = useState(ManageAccessTab.Roles);

  const { handleSubmit } = useFormContext<CreateApiKeyFields>();

  const { onCreate, loading } = useCreateApiKey({
    callback: onSuccess,
  });

  const isUserManagementActive = activationStatus === UserManagementActivationStatus.ACTIVE;

  const onSubmit: SubmitHandler<CreateApiKeyFields> = (formFields) => {
    const input = {
      admin: false,
      accessRules: [],
      machine: null,
      name: formFields.name.trim(),
      teams: formFields.groups?.map((group) => group.value) || [],
      type: formFields.type,
      oidc:
        formFields.type === ApiKeyType.Oidc
          ? {
              clientId: formFields.oidcClientId,
              issuer: formFields.oidcIssuer,
              subjectExpression: formFields.oidcSubjectExpression.trim(),
              claimMappings: (() => {
                const filtered = formFields.claimMapping.filter(
                  (claim) => claim.name.trim() && claim.value.trim()
                );
                return filtered.length > 0 ? { entries: filtered } : null;
              })(),
            }
          : null,
      description: formFields.description.trim(),
      roleBindings: formFields.assignedRoles,
      expiresAt: formFields.expiresAt ? getUnixTime(formFields.expiresAt) : null,
    };
    onCreate(input);
  };

  const onInvalid = useCallback((errors: FieldErrors<CreateApiKeyFields>) => {
    if (errors.assignedRoles) {
      setActiveTab(ManageAccessTab.Roles);
    }
  }, []);

  const handleCreate = () => {
    handleSubmit(onSubmit, onInvalid)();
  };

  return (
    <>
      <FullScreenModalBody>
        <FullScreenModalBodyHeader
          title="Manage access"
          description="Assign roles and IdP groups to control what this API key can access."
        />

        <SegmentedControl
          options={MANAGE_ACCESS_TAB_OPTIONS}
          value={activeTab}
          onChange={setActiveTab}
          margin="0 0 large 0"
        />

        <div hidden={activeTab !== ManageAccessTab.Roles}>
          {isUserManagementActive ? (
            <FormFieldRoleAssignment
              name="assignedRoles"
              availableRoles={availableRoles}
              loading={rolesLoading}
              analyticsPage={AnalyticsPageOrganization.OrganizationApiKeyNew}
            />
          ) : (
            <Controller
              name="assignedRoles"
              render={({ field }) => (
                <FormToggleField
                  variant="checkbox"
                  title="Root Admin Access (Bypass Login Policy)"
                  description="Grants unrestricted access to the platform — even if login policies are broken or misconfigured. Use only as a last resort. This key bypasses all access controls and should be stored securely offline."
                  checked={field.value.length > 0}
                  onChange={(checked) => {
                    if (checked && spaceAdminRole) {
                      field.onChange([{ spaceID: "root", roleID: spaceAdminRole.id }]);
                    } else {
                      field.onChange([]);
                    }
                  }}
                  disabled={rolesLoading || !spaceAdminRole}
                  titleSuffix={
                    <Typography tag="span" variant="p-body3" color="secondary">
                      (Optional)
                    </Typography>
                  }
                />
              )}
            />
          )}
        </div>

        <div hidden={activeTab !== ManageAccessTab.IdpGroups}>
          <FormFieldApiKeyGroups name="groups" activationStatus={activationStatus} isOptional />
        </div>
      </FullScreenModalBody>

      <ApiKeyCreateFooter onCreateClick={handleCreate} loading={loading} />
    </>
  );
};

export default ApiKeyCreateStepManageAccess;
